import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { useTranslation } from "react-i18next";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PRICE_IDS = {
  standard: process.env.REACT_APP_STRIPE_PRICE_STANDARD,
  premium: process.env.REACT_APP_STRIPE_PRICE_PREMIUM,
  art_advisor: process.env.REACT_APP_STRIPE_PRICE_ART_ADVISOR,
  art_advisor_premium: process.env.REACT_APP_STRIPE_PRICE_ART_ADVISOR_PREMIUM,
  family_office: process.env.REACT_APP_STRIPE_PRICE_FAMILY_OFFICE,
  fondation: process.env.REACT_APP_STRIPE_PRICE_FONDATION,
};

// Vérifier si l'offre d'essai est active
const TRIAL_DEADLINE = "2026-02-15T23:59:59Z";
const isTrialActive = new Date() < new Date(TRIAL_DEADLINE);

const collectorPlans = [
  {
    id: "standard",
    title: "Standard",
    price: "29 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.standard.tagline",
    features: [
      "pricing.plan.standard.feature1",
      "pricing.plan.standard.feature2",
      "pricing.plan.standard.feature3",
      "pricing.plan.standard.feature4",
    ],
    cta: "pricing.plan.standard.cta",
    highlight: false,
    trial: isTrialActive,
  },
  {
    id: "premium",
    title: "Premium",
    price: "199 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.premium.tagline",
    features: [
      "pricing.plan.premium.feature1",
      "pricing.plan.premium.feature2",
      "pricing.plan.premium.feature3",
      "pricing.plan.premium.feature4",
      "pricing.plan.premium.feature5",
    ],
    cta: "pricing.plan.premium.cta",
    highlight: true,
    trial: false,
  },
];

const professionalPlans = [
  {
    id: "art_advisor",
    title: "Art Advisor",
    price: "99 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.artAdvisor.tagline",
    features: [
      "pricing.plan.artAdvisor.feature1",
      "pricing.plan.artAdvisor.feature2",
      "pricing.plan.artAdvisor.feature3",
      "pricing.plan.artAdvisor.feature4",
      "pricing.plan.artAdvisor.feature5",
    ],
    cta: "pricing.plan.artAdvisor.cta",
    highlight: false,
    badge: "pricing.plan.artAdvisor.badge",
  },
  {
    id: "art_advisor_premium",
    title: "Art Advisor Premium",
    price: "199 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.artAdvisorPremium.tagline",
    features: [
      "pricing.plan.artAdvisorPremium.feature1",
      "pricing.plan.artAdvisorPremium.feature2",
      "pricing.plan.artAdvisorPremium.feature3",
      "pricing.plan.artAdvisorPremium.feature4",
      "pricing.plan.artAdvisorPremium.feature5",
    ],
    cta: "pricing.plan.artAdvisorPremium.cta",
    highlight: true,
    badge: "pricing.plan.artAdvisorPremium.badge",
  },
  {
    id: "family_office",
    title: "Family Office",
    price: "299 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.familyOffice.tagline",
    features: [
      "pricing.plan.familyOffice.feature1",
      "pricing.plan.familyOffice.feature2",
      "pricing.plan.familyOffice.feature3",
      "pricing.plan.familyOffice.feature4",
      "pricing.plan.familyOffice.feature5",
      "pricing.plan.familyOffice.feature6",
    ],
    cta: "pricing.plan.familyOffice.cta",
    highlight: false,
    badge: "pricing.plan.familyOffice.badge",
  },
  {
    id: "fondation",
    title: "Fondation",
    price: "299 €",
    period: "pricing.period.month",
    tagline: "pricing.plan.fondation.tagline",
    features: [
      "pricing.plan.fondation.feature1",
      "pricing.plan.fondation.feature2",
      "pricing.plan.fondation.feature3",
      "pricing.plan.fondation.feature4",
      "pricing.plan.fondation.feature5",
      "pricing.plan.fondation.feature6",
    ],
    cta: "pricing.plan.fondation.cta",
    highlight: false,
    badge: "pricing.plan.fondation.badge",
  },
];

// Helper pour sécuriser le parsing JSON
async function safeJson(res) {
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("application/json")) {
    const text = await res.text();
    throw new Error(`Réponse non-JSON (${res.status}): ${text.slice(0, 120)}…`);
  }
  return res.json();
}

export default function Pricing() {
  const { isAuthenticated, session, user } = useAuth();
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  // Récupérer le plan actuel du client
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Récupérer les infos du profil
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan, stripe_subscription_status, is_demo")
          .eq("id", user.id)
          .single();

        console.log("🔍 Profile data:", profile);
        console.log("📋 Plan actuel:", profile?.plan);
        console.log("✅ Status:", profile?.stripe_subscription_status);
        console.log("🎭 Is Demo:", profile?.is_demo);

        // Pour les comptes démo : afficher le plan s'il existe, peu importe le status
        if (profile?.is_demo && profile?.plan) {
          setCurrentPlan(profile.plan);
          console.log("✅ Demo account - Current plan set to:", profile.plan);
        }
        // Pour les comptes réels : vérifier que l'abonnement est actif
        else if (profile && (profile.stripe_subscription_status === 'active' || profile.stripe_subscription_status === 'trialing')) {
          setCurrentPlan(profile.plan);
          console.log("✅ Real account - Current plan set to:", profile.plan);
        } else {
          console.log("❌ No active subscription");
        }
      } catch (e) {
        console.error("Erreur récupération plan:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentPlan();
  }, [user]);

  const handleContactExpert = async () => {
    if (!user) {
      return navigate("/contact");
    }

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("plan, role")
        .eq("id", user.id)
        .single();

      const plan = profile?.plan || "standard";
      const params = `?plan=${plan}&from=pricing`;
      
      navigate(`/contact${params}`);
    } catch (error) {
      navigate("/contact");
    }
  };

  const handleSelect = async (planId) => {
    // Non connecté → inscription avec le plan sélectionné
    if (!isAuthenticated) {
      return navigate(`/register?plan=${planId}`);
    }

    // Connecté : vérifier si c'est un compte démo
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_demo")
        .eq("id", user.id)
        .single();

      // Si compte démo, juste mettre à jour le plan en base sans passer par Stripe
      if (profile?.is_demo) {
        const planNamesDemo = {
          standard: "Standard (29€)",
          premium: "Premium (199€)",
        };
        const confirmed = window.confirm(
          t("pricing.alert.demo.change", { plan: planNamesDemo[planId] })
        );
        if (!confirmed) return;

        // Mettre à jour le plan via RPC (le trigger anti-escalade bloque l'update direct)
        const { error } = await supabase.rpc("demo_set_plan", { p_plan: planId });

        if (error) {
          alert(t("pricing.alert.error.change", { error: error.message }));
        } else {
         alert(t("pricing.alert.demo.success"));
          window.location.reload();
        }
        return;
      }

      // ── Facturation électronique B2B : capter l'identité entreprise avant un plan pro ──
      // Identifiant selon le pays : SIREN (France) OU n° de TVA intracommunautaire (UE).
      const B2B_PLANS = ["art_advisor", "art_advisor_premium", "family_office", "fondation"];
      if (B2B_PLANS.includes(planId)) {
        const { data: bill } = await supabase
          .from("profiles")
          .select("siren, vat_number")
          .eq("id", user.id)
          .single();
        if (!bill?.siren && !bill?.vat_number) {
          const goFill = window.confirm(
            "Ce plan est facturé à une entreprise. Pour émettre une facture professionnelle conforme " +
            "(facturation électronique obligatoire), nous avons besoin de l'identifiant de votre société : " +
            "SIREN en France, ou numéro de TVA intracommunautaire dans l'UE.\n\n" +
            "OK : le renseigner maintenant · Annuler : continuer sans."
          );
          if (goFill) { navigate("/profil#facturation"); return; }
        }
      }

      // Pour les comptes réels, continuer le flux Stripe normal
      const stripe = await stripePromise;
      const priceId = PRICE_IDS[planId];

      if (!priceId) {
        console.error("Missing priceId for", planId);
        return;
      }

      const token = session?.access_token || "";

      // Vérifier l'abonnement actuel
      const checkRes = await fetch("/api/check-subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { hasSubscription } = await safeJson(checkRes);

      if (hasSubscription) {
        // Changement de forfait existant
        const planNames = {
          standard: "Standard (29€)",
          premium: "Premium (199€)",
          art_advisor: "Art Advisor (99€)",
          art_advisor_premium: "Art Advisor Premium (199€)",
          family_office: "Family Office (299€/mois)",
          fondation: "Fondation (299€/mois)",
        };

        const confirmed = window.confirm(
          t("pricing.alert.confirm.change", { plan: planNames[planId] })
        );
        if (!confirmed) return;

        const res = await fetch("/api/change-subscription-plan", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ newPriceId: priceId }),
        });

        const { error } = await safeJson(res);

        if (error) {
          alert(t("pricing.alert.error.subscription", { error }));
        } else {
          alert(t("pricing.alert.success.change"));
          // Recharger la page pour mettre à jour l'affichage
          window.location.reload();
        }
      } else {
        // Nouvel abonnement — via l'edge function Supabase.
        // ⚠️ NE PAS appeler /api/create-checkout-session : /api est proxifié vers
        // le backend Python (→ 404). La fonction vit chez Supabase ; invoke()
        // tape la bonne URL avec le JWT automatiquement.
        const { data, error } = await supabase.functions.invoke("create-checkout-session", {
          body: { priceId, plan: planId },
        });

        if (error || data?.error) {
          alert(t("pricing.alert.error.subscription", { error: data?.error || error?.message }));
          return;
        }

        if (data?.url) {
          window.location.href = data.url;
        } else if (data?.sessionId) {
          await stripe.redirectToCheckout({ sessionId: data.sessionId });
        }
      }
    } catch (err) {
      console.error("Erreur lors de la sélection:", err);
      alert(t("pricing.alert.error.generic"));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* Header principal */}
        <div className="text-center mb-20">
          <h1
            className="text-5xl md:text-6xl font-serif mb-6 tracking-tight"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {t("pricing.hero.title")}
          </h1>
          <p className="text-xl text-neutral-500 max-w-2xl mx-auto leading-relaxed">
           {t("pricing.hero.subtitle")}
          </p>
        </div>

        {/* Message pour client avec plan actif */}
        {currentPlan && !loading && (
          <div className="max-w-3xl mx-auto mb-12 p-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                ✓
              </div>
              <div>
                <h3 className="font-semibold text-green-900 mb-1">
                  {t("pricing.currentPlan.bannerTitle")}
                </h3>
                <p className="text-sm text-green-800">
                  {t("pricing.currentPlan.text", { plan: currentPlan })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Badge offre de lancement */}
        {isTrialActive && (
          <div className="max-w-3xl mx-auto mb-16 p-6 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 border border-neutral-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-xl">
                🎁
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  {t("pricing.trial.title")}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {t("pricing.trial.text")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Section Collectionneurs */}
        <div className="mb-24">
          <div className="mb-12">
            <h2
              className="text-3xl font-serif mb-3"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("pricing.section.collectors.title")}
            </h2>
            <p className="text-neutral-500 text-lg">
              {t("pricing.section.collectors.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl">
            {collectorPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onSelect={() => handleSelect(plan.id)}
                isCurrentPlan={currentPlan === plan.id}
                currentPlan={currentPlan}
                loading={loading}
              />
            ))}
          </div>
        </div>

        {/* Séparateur élégant */}
        <div className="my-24 border-t border-neutral-200"></div>

        {/* Section Professionnels */}
        <div>
          <div className="mb-12">
            <h2
              className="text-3xl font-serif mb-3"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("pricing.section.pro.title")}
            </h2>
            <p className="text-neutral-500 text-lg">
              {t("pricing.section.pro.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {professionalPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onSelect={() => handleSelect(plan.id)}
                isCurrentPlan={currentPlan === plan.id}
                currentPlan={currentPlan}
                loading={loading}
              />
            ))}
          </div>
        </div>

        {/* Section comparative */}
        <div className="mt-24 pt-16 border-t border-neutral-200">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-serif mb-4">{t("pricing.compare.title")}</h3>
            <p className="text-neutral-500 mb-8">
              {t("pricing.compare.subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate("/plans/compare-plans")}
                className="px-8 py-3 rounded-full border-2 border-black text-black font-medium hover:bg-black hover:text-white transition-all duration-300"
              >
                {t("pricing.compare.button.compare")}
              </button>
              <button
                onClick={handleContactExpert}
                className="px-8 py-3 rounded-full bg-black text-white font-medium hover:bg-neutral-800 transition-all duration-300"
              >
                {t("pricing.compare.button.expert")}
              </button>
            </div>
          </div>
        </div>

        {/* FAQ rapide */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h4 className="text-xl font-semibold mb-6 text-center">{t("pricing.faq.title")}</h4>
          <div className="space-y-4">
            <FAQItem
              question={t("pricing.faq.q1")}
              answer={t("pricing.faq.a1")}
            />
            <FAQItem
              question={t("pricing.faq.q2")}
              answer={t("pricing.faq.a2")}
            />
            <FAQItem
              question={t("pricing.faq.q3")}
              answer={t("pricing.faq.a3")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant carte de plan réutilisable
function PlanCard({ plan, onSelect, isCurrentPlan, currentPlan, loading }) {
  // Debug
  console.log(`🎯 PlanCard ${plan.id}:`, { isCurrentPlan, currentPlan, loading });
  const { t } = useTranslation();
  // Ordre des plans pour déterminer upgrade/downgrade
  const planOrder = {
    standard: 1,
    premium: 2,
    art_advisor: 3,
    art_advisor_premium: 4,
    family_office: 5,
  };

  // Déterminer le texte du bouton
  const getButtonText = () => {
    if (isCurrentPlan) return t("pricing.plan.button.current");
    
    // Si on a un plan actuel, déterminer si c'est un upgrade ou downgrade
    if (currentPlan) {
      const currentOrder = planOrder[currentPlan] || 0;
      const targetOrder = planOrder[plan.id] || 0;
      
      if (targetOrder > currentOrder) {
        // Upgrade
        return t("pricing.plan.button.upgrade", { title: plan.title });
      } else if (targetOrder < currentOrder) {
        // Downgrade
        return t("pricing.plan.button.downgrade", { title: plan.title });
      }
    }
    
    // Par défaut, utiliser le CTA du plan
    return t(plan.cta);
  };

  return (
    <div
      className={`
        relative rounded-2xl p-8 flex flex-col transition-all duration-300 border-2
        ${
          plan.highlight
            ? "border-black shadow-xl bg-neutral-50 scale-105"
            : isCurrentPlan
            ? "border-green-500 shadow-lg bg-green-50"
            : "border-neutral-200 hover:border-neutral-400 hover:shadow-lg bg-white"
        }
      `}
    >
      {/* Badge Plan Actuel */}
      {isCurrentPlan && (
        <div className="absolute top-6 right-6">
          <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full tracking-wider">
            {t("pricing.plan.badge.current")}
          </span>
        </div>
      )}

      {/* Badge Pro/Enterprise */}
      {plan.badge && !isCurrentPlan && (
        <div className="absolute top-6 right-6">
          <span className="px-3 py-1 bg-black text-white text-xs font-bold rounded-full tracking-wider">
            {t(plan.badge)}
          </span>
        </div>
      )}

      {/* Badge essai gratuit */}
      {plan.trial && !isCurrentPlan && (
        <div className="mb-4">
          <span className="px-3 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold rounded-full">
            {t("pricing.plan.trialBadge")}
          </span>
        </div>
      )}

      {/* Titre et prix */}
      <div className="mb-6">
        <h3
          className="text-2xl font-serif mb-2"
          style={{ fontFamily: "Georgia, serif" }}
        >
          {plan.title}
        </h3>
        <p className="text-neutral-500 text-sm leading-relaxed mb-4">
          {t(plan.tagline)}
        </p>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold">{plan.price}</span>
          {plan.period && (
            <span className="text-neutral-500 text-lg">{t(plan.period)}</span>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="flex-1 mb-8">
        <ul className="space-y-3">
          {plan.features.map((featureKey, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-black flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-neutral-700 leading-relaxed">
                {t(featureKey)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <button
        onClick={onSelect}
        disabled={isCurrentPlan}
        className={`
          w-full py-4 rounded-full font-medium transition-all duration-300
          ${
            isCurrentPlan
              ? "bg-green-600 text-white cursor-not-allowed opacity-75"
              : plan.highlight
              ? "bg-black text-white hover:bg-neutral-800"
              : "border-2 border-black text-black hover:bg-black hover:text-white"
          }
          ${loading && !isCurrentPlan ? "opacity-50 cursor-wait" : ""}
        `}
      >
        {getButtonText()}
      </button>

      {/* Indication pour plan actuel */}
      {isCurrentPlan && (
        <p className="mt-3 text-xs text-center text-neutral-500">
          {t("pricing.plan.currentNote")}
        </p>
      )}
    </div>
  );
}

// Composant FAQ Item
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-neutral-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left flex items-start justify-between gap-4 group"
      >
        <span className="font-medium group-hover:text-neutral-600 transition-colors">
          {question}
        </span>
        <svg
          className={`w-5 h-5 flex-shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <p className="mt-3 text-neutral-600 leading-relaxed">{answer}</p>
      )}
    </div>
  );
}

// ============================================================================
// PAGE BILLING PORTAL - Exportée pour être utilisée ailleurs
// ============================================================================

export function BillingPortal() {
  const { isAuthenticated, session, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checking, setChecking] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const { t } = useTranslation();
  useEffect(() => {
    const checkSub = async () => {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        // 1) Vérifier si c'est un profil démo
        const { data: profile } = await supabase
          .from("profiles")
          .select("is_demo, stripe_subscription_status, plan, plan_started_at")
          .eq("id", user.id)
          .single();

        setProfileData(profile);
        setIsDemo(profile?.is_demo || false);

        // 2) Si profil démo, utiliser les données du profil directement
        if (profile?.is_demo) {
          const hasActiveSub = profile.stripe_subscription_status === 'active' || 
                               profile.stripe_subscription_status === 'trialing';
          setHasSubscription(hasActiveSub);
          setSubscriptionStatus(profile.stripe_subscription_status || 'none');
        } else {
          // 3) Sinon, vérifier via l'API normale
          if (session?.access_token) {
            const res = await fetch("/api/check-subscription", {
              headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const data = await safeJson(res);
            setHasSubscription(data.hasSubscription);
            setSubscriptionStatus(data.status);
          }
        }
      } catch (e) {
        console.error("Erreur vérification abonnement:", e);
      } finally {
        setChecking(false);
      }
    };

    checkSub();
  }, [user, session]);

  const openPortal = async () => {
    // Bloquer l'accès pour les profils démo
    if (isDemo) {
      alert(t("billing.alert.demoDisabled"));
      return;
    }

    if (!hasSubscription) {
      alert(t("billing.alert.noSubscription"));
      return navigate("/pricing");
    }

    try {
      setLoading(true);
      // ⚠️ NE PAS appeler /api/create-billing-portal-session : /api est proxifié vers
      // le backend Python (→ 404). La fonction vit chez Supabase ; invoke() tape la
      // bonne URL avec le JWT automatiquement (même piège que create-checkout-session).
      const { data, error: fnError } = await supabase.functions.invoke(
        "create-billing-portal-session",
        {}
      );

      const url = data?.url;
      const error = fnError?.message || data?.error;

      if (error || !url) {
        throw new Error(error || "Aucune URL renvoyée");
      }

      window.location.href = url;
    } catch (e) {
      alert(t("billing.alert.portalOpenFailed", { error: e.message }));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-neutral-50">
        <div className="max-w-md w-full bg-white p-10 rounded-2xl shadow-sm text-center">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">🔒</span>
          </div>
<h1 className="text-2xl font-serif mb-3">
  {t("billing.portal.title")}
</h1>
<p className="text-neutral-600 mb-8">
  {t("billing.portal.requireLogin")}
</p>
          <button
            className="btn-artsy btn-primary-artsy w-full"
            onClick={() => navigate("/login?next=/billing")}
          >
            {t("billing.portal.loginButton")}

          </button>

        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-neutral-600">{t("billing.portal.checking")}</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // AFFICHAGE POUR PROFIL DÉMO
  // ============================================================
if (isDemo) {
  const statusLabel =
    {
      active: t("billing.demo.status.active"),
      trialing: t("billing.demo.status.trialing"),
      past_due: t("billing.demo.status.past_due"),
      canceled: t("billing.demo.status.canceled"),
      none: t("billing.demo.status.none"),
    }[subscriptionStatus] || subscriptionStatus;

    return (
      <div className="min-h-screen bg-neutral-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Bannière démo */}
          <div className="mb-8 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-block h-3 w-3 rounded-full bg-indigo-500" />
                <div>
<p className="font-semibold text-indigo-900">
  {t("billing.demo.bannerTitle")}
</p>
<p className="text-sm text-indigo-700">
  {t("billing.demo.bannerText")}
                  </p>
                </div>
              </div>
              <span className="inline-block rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold tracking-wider text-white shadow">
{t("billing.demo.badge")}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-10">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-serif mb-2">
  {t("billing.demo.pageTitle")}
</h1>

              {hasSubscription && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                  {statusLabel} (simulé)
                </div>
              )}
            </div>

            {!hasSubscription ? (
              // Cas : Pas d'abonnement simulé
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">🎭</span>
                </div>
<h2 className="text-xl font-semibold mb-3">
  {t("billing.demo.noSub.title")}
</h2>
<p className="text-neutral-600 mb-8 max-w-md mx-auto">
  {t("billing.demo.noSub.text")}
</p>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6 max-w-xl mx-auto">
                  <p className="text-sm text-blue-800">
  {t("billing.demo.noSub.note")}
</p>
                </div>

                <button
                  className="btn-artsy btn-secondary-artsy px-8"
                  onClick={() => navigate("/pricing")}
                >

{t("billing.demo.noSub.button")}
                </button>
              </div>
            ) : (
              // Cas : Abonnement simulé actif
              <div>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">{t("billing.demo.withSub.plan")}</p>
                    <p className="text-lg font-semibold capitalize">{profileData?.plan || "Standard"}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">{t("billing.demo.withSub.price")}</p>
                    <p className="text-lg font-semibold">{t("billing.demo.withSub.priceValue")}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">{t("billing.demo.withSub.nextPayment")}</p>
                    <p className="text-lg font-semibold">N/A (démo)</p>
                  </div>
                </div>

                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg mb-8">
                  <p className="text-sm text-indigo-800">
                    {t("billing.demo.withSub.note")}
                  </p>
                </div>

                <p className="text-neutral-600 mb-8 leading-relaxed">
                  {t("billing.demo.withSub.explainer")}
                </p>

                <div className="grid md:grid-cols-3 gap-4">
                  <button 
                    className="btn-artsy btn-secondary-artsy py-4 opacity-60 cursor-not-allowed" 
                    disabled
                    title="Fonctionnalité désactivée en mode démo"
                  >
                    {t("billing.demo.withSub.button.portalDisabled")}
                  </button>

                  <button
                    className="btn-artsy btn-secondary-artsy py-4"
                    onClick={() => navigate("/pricing")}
                  >
                    {t("billing.demo.withSub.button.seeOffers")}
                  </button>

                  <button
                    className="btn-artsy btn-primary-artsy py-4"
                    onClick={() => navigate("/invoices")}
                  >
                    {t("billing.demo.withSub.button.invoices")}
                  </button>
                </div>

                <div className="mt-10 pt-8 border-t border-neutral-200">
<h3 className="font-semibold mb-4">
  {t("billing.demo.withSub.aboutTitle")}
</h3>
<p className="text-sm text-neutral-600 mb-4">
  {t("billing.demo.withSub.aboutText")}
</p>                  <a 
                    href="/register" 
                    className="text-sm font-medium underline hover:text-black"
                  >
                    {t("billing.demo.withSub.aboutLink")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // AFFICHAGE POUR UTILISATEUR RÉEL
  // ============================================================
  return (
    <div className="min-h-screen bg-neutral-50 py-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-3xl shadow-lg p-10">
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-serif mb-2">
  {t("billing.real.pageTitle")}
</h1>

            {subscriptionStatus && hasSubscription && (
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                subscriptionStatus === 'active' 
                  ? 'bg-green-100 text-green-800'
                  : subscriptionStatus === 'trialing'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-amber-100 text-amber-800'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  subscriptionStatus === 'active' 
                    ? 'bg-green-600'
                    : subscriptionStatus === 'trialing'
                    ? 'bg-blue-600'
                    : 'bg-amber-600'
                }`}></span>
                {subscriptionStatus === "trialing"
  ? t("billing.status.trialing")
  : t("billing.status.active")}

              </div>
            )}
          </div>

          {!hasSubscription ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">📋</span>
              </div>
              <h2 className="text-xl font-semibold mb-3">
  {t("billing.real.noSub.title")}
</h2>
<p className="text-neutral-600 mb-8 max-w-md mx-auto">
  {t("billing.real.noSub.text")}
</p>
              <button
                className="btn-artsy btn-primary-artsy px-8"
                onClick={() => navigate("/pricing")}
              >
                {t("billing.real.noSub.button")}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-neutral-600 mb-8 leading-relaxed">
                {t("billing.real.explainer")}
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                <button 
                  className="btn-artsy btn-primary-artsy py-4" 
                  onClick={openPortal}
                  disabled={loading}
                >
                  {loading ? t("billing.real.button.portal.loading")
  : t("billing.real.button.portal")}
                </button>

                <button
                  className="btn-artsy btn-secondary-artsy py-4"
                  onClick={() => navigate("/pricing")}
                >
                  {t("billing.real.button.changePlan")}
                </button>

                <button
                  className="btn-artsy btn-secondary-artsy py-4"
                  onClick={() => navigate("/invoices")}
                >
                  {t("billing.real.button.invoices")}
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-neutral-200">
                <h3 className="font-semibold mb-4">{t("billing.real.help.title")}</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  {t("billing.real.help.text")}
                </p>
                <a 
                  href="/contact" 
                  className="text-sm font-medium underline hover:text-black"
                >
                  {t("billing.real.help.link")}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}